import React, { useState } from 'react';
import { AlertTriangle, ShieldCheck } from 'lucide-react';
import {
  PhotoLabels,
  PhotoType,
  SettlementType,
  HousingType,
  HouseCondition,
  RoofType,
  StreetType,
  VehiclePresence,
  CleanlinessLevel,
  PopulationDensity,
  EconomicActivity,
  ImageQuality,
  IncomeProxy,
  PHOTO_TYPE_LABELS,
} from '../../../types/annotation';

interface PhotoLabelFormProps {
  photoType?: PhotoType | null;
  labels: Partial<PhotoLabels>;
  onChange: (updates: Partial<PhotoLabels>) => void;
  onPhotoTypeChange?: (type: PhotoType | null) => void;
  disabled?: boolean;
  onSupervisorVerify?: (verified: boolean, notes: string) => void;
}

// ─── Display Maps ─────────────────────────────────────────────────────────────

const SETTLEMENT_LABELS: Record<SettlementType, string> = {
  dense_urban: 'Dense Urban',
  urban: 'Urban',
  small_town: 'Small Town',
  semi_urban: 'Semi-Urban',
  village: 'Village',
  remote_rural: 'Remote Rural',
};

const HOUSING_LABELS: Record<HousingType, string> = {
  apartment_flat: 'Apartment / Flat',
  plastered_house: 'Plastered House',
  brick_house: 'Brick House',
  mixed_construction: 'Mixed Construction',
  mud_house: 'Mud House',
  temporary_structure: 'Temporary Structure',
  slum_dwelling: 'Slum Dwelling',
};

const HOUSE_CONDITION_LABELS: Record<HouseCondition, string> = {
  excellent: 'Excellent',
  good: 'Good',
  average: 'Average',
  poor: 'Poor',
  dilapidated: 'Dilapidated',
};

const ROOF_LABELS: Record<RoofType, string> = {
  concrete_slab: 'Concrete Slab',
  tin_metal_sheet: 'Tin / Metal Sheet',
  tile_roof: 'Tile Roof',
  thatch: 'Thatch',
  mixed_materials: 'Mixed Materials',
};

const STREET_LABELS: Record<StreetType, string> = {
  rcc_road: 'RCC Road',
  paved_road: 'Paved Road',
  dirt_road: 'Dirt Road',
  narrow_lane: 'Narrow Lane',
  highway: 'Highway',
};

const VEHICLE_LABELS: Record<VehiclePresence, string> = {
  none: 'None',
  bicycle: 'Bicycle',
  motorcycle: 'Motorcycle',
  auto_rickshaw: 'Auto Rickshaw',
  car: 'Car',
  multiple_vehicles: 'Multiple Vehicles',
};

const CLEANLINESS_LABELS: Record<CleanlinessLevel, string> = {
  very_clean: 'Very Clean',
  moderately_clean: 'Moderately Clean',
  some_litter: 'Some Litter',
  dirty: 'Dirty',
  heavily_polluted: 'Heavily Polluted',
};

const DENSITY_LABELS: Record<PopulationDensity, string> = {
  sparse: 'Sparse',
  moderate: 'Moderate',
  dense: 'Dense',
  very_dense: 'Very Dense',
};

const ECONOMIC_LABELS: Record<EconomicActivity, string> = {
  none: 'None',
  agriculture: 'Agriculture',
  small_shops: 'Small Shops',
  street_vendors: 'Street Vendors',
  market_area: 'Market Area',
  industrial_activity: 'Industrial Activity',
};

const IMAGE_QUALITY_LABELS: Record<ImageQuality, string> = {
  clear: 'Clear',
  blurred: 'Blurred',
  low_light: 'Low Light',
  overexposed: 'Overexposed',
  partially_obstructed: 'Partially Obstructed',
};

const INCOME_LABELS: Record<IncomeProxy, string> = {
  very_low: 'Very Low',
  low: 'Low',
  lower_middle: 'Lower Middle',
  middle: 'Middle',
  upper_middle: 'Upper Middle',
};

const INFRASTRUCTURE_OPTIONS = [
  'Electric poles',
  'Water pipes',
  'Drainage',
  'Street lights',
  'Internet cables',
  'Solar panels',
];

const OBJECT_PRESENCE_OPTIONS = [
  'Water tank',
  'Satellite dish',
  'Air conditioner',
  'Farm equipment',
  'Livestock',
  'Construction materials',
];

// ─── Helper components ────────────────────────────────────────────────────────

interface FieldProps {
  label: string;
  children: React.ReactNode;
  warning?: boolean;
  warningText?: string;
}

const Field: React.FC<FieldProps> = ({ label, children, warning, warningText }) => (
  <div>
    <div className="flex items-center gap-1.5 mb-1.5">
      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
        {label}
      </label>
      {warning && (
        <span className="flex items-center gap-0.5 text-xs text-amber-600 font-medium">
          <AlertTriangle className="w-3 h-3" />
          {warningText ?? 'Subjective'}
        </span>
      )}
    </div>
    {children}
  </div>
);

function makeSelectOptions<T extends string>(
  map: Record<T, string>,
): React.ReactElement[] {
  return (Object.keys(map) as T[]).map((k) => (
    <option key={k} value={k}>
      {map[k]}
    </option>
  ));
}

const SELECT_CLASS =
  'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-white disabled:opacity-50 disabled:cursor-not-allowed';

// ─── Main component ───────────────────────────────────────────────────────────

const PhotoLabelForm: React.FC<PhotoLabelFormProps> = ({
  photoType,
  labels,
  onChange,
  onPhotoTypeChange,
  disabled = false,
  onSupervisorVerify,
}) => {
  const [supervisorNotes, setSupervisorNotes] = useState(labels.supervisorNotes ?? '');

  const toggleMulti = (field: 'infrastructure' | 'objectPresence', val: string) => {
    const current: string[] = (labels[field] as string[]) ?? [];
    const next = current.includes(val)
      ? current.filter((v) => v !== val)
      : [...current, val];
    onChange({ [field]: next });
  };

  return (
    <div className="space-y-4">
      {/* Photo Type */}
      <Field label="Photo Type">
        <select
          value={photoType ?? ''}
          onChange={(e) => onPhotoTypeChange?.((e.target.value as PhotoType) || null)}
          disabled={disabled || !onPhotoTypeChange}
          className={SELECT_CLASS}
        >
          <option value="">— Select —</option>
          {(Object.keys(PHOTO_TYPE_LABELS) as PhotoType[]).map((k) => (
            <option key={k} value={k}>
              {PHOTO_TYPE_LABELS[k]}
            </option>
          ))}
        </select>
      </Field>

      {/* Description */}
      <Field label="Description">
        <textarea
          value={labels.description ?? ''}
          onChange={(e) => onChange({ description: e.target.value })}
          disabled={disabled}
          rows={3}
          placeholder="Describe the image..."
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </Field>

      {/* Settlement Type */}
      <Field label="Settlement Type">
        <select
          value={labels.settlementType ?? ''}
          onChange={(e) =>
            onChange({ settlementType: (e.target.value as SettlementType) || null })
          }
          disabled={disabled}
          className={SELECT_CLASS}
        >
          <option value="">— Select —</option>
          {makeSelectOptions(SETTLEMENT_LABELS)}
        </select>
      </Field>

      {/* Housing Type */}
      <Field label="Housing Type">
        <select
          value={labels.housingType ?? ''}
          onChange={(e) =>
            onChange({ housingType: (e.target.value as HousingType) || null })
          }
          disabled={disabled}
          className={SELECT_CLASS}
        >
          <option value="">— Select —</option>
          {makeSelectOptions(HOUSING_LABELS)}
        </select>
      </Field>

      {/* House Condition */}
      <Field label="House Condition">
        <select
          value={labels.houseCondition ?? ''}
          onChange={(e) =>
            onChange({ houseCondition: (e.target.value as HouseCondition) || null })
          }
          disabled={disabled}
          className={SELECT_CLASS}
        >
          <option value="">— Select —</option>
          {makeSelectOptions(HOUSE_CONDITION_LABELS)}
        </select>
      </Field>

      {/* Roof Type */}
      <Field label="Roof Type">
        <select
          value={labels.roofType ?? ''}
          onChange={(e) =>
            onChange({ roofType: (e.target.value as RoofType) || null })
          }
          disabled={disabled}
          className={SELECT_CLASS}
        >
          <option value="">— Select —</option>
          {makeSelectOptions(ROOF_LABELS)}
        </select>
      </Field>

      {/* Street Type */}
      <Field label="Street Type">
        <select
          value={labels.streetType ?? ''}
          onChange={(e) =>
            onChange({ streetType: (e.target.value as StreetType) || null })
          }
          disabled={disabled}
          className={SELECT_CLASS}
        >
          <option value="">— Select —</option>
          {makeSelectOptions(STREET_LABELS)}
        </select>
      </Field>

      {/* Vehicles */}
      <Field label="Vehicles">
        <select
          value={labels.vehicles ?? ''}
          onChange={(e) =>
            onChange({ vehicles: (e.target.value as VehiclePresence) || null })
          }
          disabled={disabled}
          className={SELECT_CLASS}
        >
          <option value="">— Select —</option>
          {makeSelectOptions(VEHICLE_LABELS)}
        </select>
      </Field>

      {/* Cleanliness */}
      <Field label="Cleanliness">
        <select
          value={labels.cleanliness ?? ''}
          onChange={(e) =>
            onChange({ cleanliness: (e.target.value as CleanlinessLevel) || null })
          }
          disabled={disabled}
          className={SELECT_CLASS}
        >
          <option value="">— Select —</option>
          {makeSelectOptions(CLEANLINESS_LABELS)}
        </select>
      </Field>

      {/* Population Density */}
      <Field label="Population Density">
        <select
          value={labels.populationDensity ?? ''}
          onChange={(e) =>
            onChange({ populationDensity: (e.target.value as PopulationDensity) || null })
          }
          disabled={disabled}
          className={SELECT_CLASS}
        >
          <option value="">— Select —</option>
          {makeSelectOptions(DENSITY_LABELS)}
        </select>
      </Field>

      {/* Economic Activity */}
      <Field label="Economic Activity">
        <select
          value={labels.economicActivity ?? ''}
          onChange={(e) =>
            onChange({ economicActivity: (e.target.value as EconomicActivity) || null })
          }
          disabled={disabled}
          className={SELECT_CLASS}
        >
          <option value="">— Select —</option>
          {makeSelectOptions(ECONOMIC_LABELS)}
        </select>
      </Field>

      {/* Infrastructure */}
      <Field label="Infrastructure">
        <div className="grid grid-cols-2 gap-y-1.5 gap-x-3">
          {INFRASTRUCTURE_OPTIONS.map((opt) => {
            const checked = (labels.infrastructure ?? []).includes(opt);
            return (
              <label key={opt} className={`flex items-center gap-2 text-sm cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleMulti('infrastructure', opt)}
                  disabled={disabled}
                  className="w-3.5 h-3.5 rounded border-gray-300 accent-violet-600"
                />
                <span className={checked ? 'text-violet-700 font-medium' : 'text-gray-600'}>
                  {opt}
                </span>
              </label>
            );
          })}
        </div>
      </Field>

      {/* Object Presence */}
      <Field label="Object Presence">
        <div className="grid grid-cols-2 gap-y-1.5 gap-x-3">
          {OBJECT_PRESENCE_OPTIONS.map((opt) => {
            const checked = (labels.objectPresence ?? []).includes(opt);
            return (
              <label key={opt} className={`flex items-center gap-2 text-sm cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleMulti('objectPresence', opt)}
                  disabled={disabled}
                  className="w-3.5 h-3.5 rounded border-gray-300 accent-violet-600"
                />
                <span className={checked ? 'text-violet-700 font-medium' : 'text-gray-600'}>
                  {opt}
                </span>
              </label>
            );
          })}
        </div>
      </Field>

      {/* Image Quality */}
      <Field label="Image Quality">
        <select
          value={labels.imageQuality ?? ''}
          onChange={(e) =>
            onChange({ imageQuality: (e.target.value as ImageQuality) || null })
          }
          disabled={disabled}
          className={SELECT_CLASS}
        >
          <option value="">— Select —</option>
          {makeSelectOptions(IMAGE_QUALITY_LABELS)}
        </select>
      </Field>

      {/* Socioeconomic Estimate */}
      <Field
        label="Socioeconomic Estimate"
        warning
        warningText="Subjective"
      >
        <select
          value={labels.incomeProxy ?? ''}
          onChange={(e) =>
            onChange({ incomeProxy: (e.target.value as IncomeProxy) || null })
          }
          disabled={disabled}
          className={SELECT_CLASS}
        >
          <option value="">— Select —</option>
          {makeSelectOptions(INCOME_LABELS)}
        </select>
      </Field>

      {/* Privacy Flag */}
      <Field label="Privacy">
        <label className={`flex items-center gap-2 text-sm cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
          <input
            type="checkbox"
            checked={labels.privacyFlag ?? false}
            onChange={(e) => onChange({ privacyFlag: e.target.checked })}
            disabled={disabled}
            className="w-4 h-4 rounded border-gray-300 accent-red-600"
          />
          <span className="text-gray-700">Faces / PII visible</span>
        </label>
      </Field>

      {/* Supervisor Verification (review mode) */}
      {onSupervisorVerify && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-green-500" />
            Supervisor Verification
          </p>

          {labels.supervisorVerified && (
            <div className="mb-3 px-3 py-2 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-green-600 shrink-0" />
              <div>
                <p className="text-xs font-medium text-green-700">Verified</p>
                {labels.verifiedAt && (
                  <p className="text-xs text-green-600">
                    {new Date(labels.verifiedAt).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          )}

          <textarea
            value={supervisorNotes}
            onChange={(e) => setSupervisorNotes(e.target.value)}
            rows={3}
            placeholder="Supervisor notes (optional)..."
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none mb-3"
          />

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onSupervisorVerify(true, supervisorNotes)}
              className="flex-1 px-3 py-1.5 text-sm rounded-lg font-medium bg-green-600 text-white hover:bg-green-700 transition-colors"
            >
              Verify
            </button>
            <button
              type="button"
              onClick={() => onSupervisorVerify(false, supervisorNotes)}
              className="flex-1 px-3 py-1.5 text-sm rounded-lg font-medium bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 transition-colors"
            >
              Flag Issue
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoLabelForm;
